import { useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import {
  ActionIcon,
  Avatar,
  Collapse,
  Divider,
  Menu,
  Text,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Copy,
  Globe,
  Mail,
  Phone,
  Plus,
  Printer,
  StickyNote,
} from 'lucide-react'
import { VerificationOfEmployment } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import DnDrawer from 'src/components/Core/DnDrawer'
import DnPrompt from 'src/components/Core/DnPrompt'
import CompletionMethodModal from 'src/components/Overlays/CompletionMethodModal/CompletionMethodModal'
import {
  toMarkVoEAsCompletedInput,
  voeCompletionContext,
} from 'src/components/Overlays/CompletionMethodModal/voeCompletionContext'
import EmailSenderModal from 'src/components/Overlays/EmailSenderModal/EmailSenderModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useAutomatedOutreachEmail } from 'src/components/VoE/AutomatedOutreachPanel/useAutomatedOutreachEmail'
import NextActionLine from 'src/components/VoE/NextActionTag/NextActionLine'
import { useNoContactGuard } from 'src/components/VoE/NoContactGuard/useNoContactGuard'
import { useScheduledOutreachGuard } from 'src/components/VoE/ScheduledOutreachGuard/useScheduledOutreachGuard'
import { useCallDrawerContext } from 'src/context/CallDrawerContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import {
  VoEAuditTrailType,
  VoEStatus,
} from 'src/graphql/types/verificationOfEmployments'
import {
  GET_VOE_ACTIONS,
  GET_VOE_ADDRESS,
  GET_VOE_CONTACT_ATTEMPT_COUNT,
  MARK_VOE_AS_COMPLETED,
  SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE,
  UPDATE_VOE,
} from 'src/graphql/voe.graphql'
import { useCompanyOutreachConfig } from 'src/hooks/useCompanyOutreachConfig'
import {
  cleanPhoneNumber,
  emailMatches,
  phoneMatches,
  sanitizeEmail,
} from 'src/lib/contact.utils'
import { calculateAverageJobDuration } from 'src/lib/employment.utils'
import {
  buildFullName,
  formatDateCustom,
  formatSnakeValue,
  getInitials,
} from 'src/lib/formatters'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import {
  formatInternationalPhoneNumber,
  unformatPhoneNumber,
} from 'src/lib/phone.utils'
import { hasNeverWorkedHereFinding } from 'src/lib/voeFindings'
import { knownPlatformLabel } from 'src/lib/voeProvider.utils'
import {
  DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  isExpiredEmployment,
  formatVerificationPeriod,
} from 'src/lib/voeVerificationPeriod'

import { buildVoEInstructionsEmailBody } from '../../lib/voeEmailTemplate'
import ApplicantInfoSection from '../ApplicantInfoSection/ApplicantInfoSection'
import {
  ACTOR_MARKER_CLASS,
  AUDIT_TRAIL_TIMESTAMP_CLASS,
  AUTOMATED_OUTREACH_LABEL,
  contactForAction,
  OUTREACH_DATE_TIME_FORMAT,
} from '../ScheduledOutreachSection/outreachActionLabels'
import ScheduledActionsTable from '../ScheduledOutreachSection/ScheduledActionsTable'
import UpcomingOutreachRow from '../ScheduledOutreachSection/UpcomingOutreachRow'
import { useOutreachActions } from '../ScheduledOutreachSection/useOutreachActions'
import VerificationDetailsSection, {
  type VoEReferral,
} from '../VerificationDetailsSection/VerificationDetailsSection'
import VoEDocumentsSection from '../VoEDocumentsSection/VoEDocumentsSection'
import VoEStatusMenu from '../VoEStatusMenu/VoEStatusMenu'

import AuditTrailRefreshButton from './AuditTrailRefreshButton'
import GenerateAuditTrailPdfButton from './GenerateAuditTrailPdfButton'
import {
  useAutomatedEmailActions,
  useFetchLatestAutomatedEmails,
} from './useAutomatedEmailActions'
import { useManualSendConfirm } from './useManualSendConfirm'
import VoeActions from './VoeActions'

const auditTrailTypeConfig: Record<
  string,
  { icon: React.ReactNode; iconClass: string; bgClass: string; label: string }
> = {
  [VoEAuditTrailType.CALL]: {
    icon: <Phone size={12} />,
    iconClass: 'text-doubleNickel-success-600',
    bgClass: 'bg-doubleNickel-success-50',
    label: 'Call attempt',
  },
  [VoEAuditTrailType.EMAIL]: {
    icon: <Mail size={12} />,
    iconClass: 'text-doubleNickel-brand-500',
    bgClass: 'bg-doubleNickel-brand-25',
    label: 'Email sent',
  },
  [VoEAuditTrailType.FAX]: {
    icon: <Printer size={12} />,
    iconClass: 'text-doubleNickel-warning-600',
    bgClass: 'bg-doubleNickel-warning-50',
    label: 'Fax sent',
  },
  [VoEAuditTrailType.NOTE]: {
    icon: <StickyNote size={12} />,
    iconClass: 'text-doubleNickel-gray-500',
    bgClass: 'bg-doubleNickel-gray-100',
    label: 'Note',
  },
  [VoEAuditTrailType.ATS]: {
    icon: <Globe size={12} />,
    iconClass: 'text-doubleNickel-blue-500',
    bgClass: 'bg-doubleNickel-blue-50',
    label: 'ATS update',
  },
  [VoEAuditTrailType.RESPONSE_RECEIVED]: {
    icon: <Check size={12} />,
    iconClass: 'text-doubleNickel-success-600',
    bgClass: 'bg-doubleNickel-success-50',
    label: 'Response received',
  },
}

const getInfoBasedOnType = (
  auditTrailLog,
  voeContext?: { companyName?: string; contactName?: string }
) => {
  switch (auditTrailLog.type) {
    case VoEAuditTrailType.CALL: {
      const parts = [
        voeContext?.companyName,
        voeContext?.contactName && `(${voeContext.contactName})`,
        auditTrailLog.phone &&
          formatInternationalPhoneNumber(auditTrailLog.phone),
      ].filter(Boolean)
      return parts.join(' ')
    }
    case VoEAuditTrailType.EMAIL:
      return auditTrailLog.email
    case VoEAuditTrailType.FAX:
      return formatInternationalPhoneNumber(auditTrailLog.fax)
    default:
      return ''
  }
}

const VOE_AGENT_AUDIT_TRAIL_TITLE = 'Voice agent call'
const VOE_AGENT_CALL_PENDING_MESSAGE = 'Call Attempt in Progress'

export const isVoEAgentAuditLog = (auditTrailLog: {
  type?: string | null
  title?: string | null
  message?: string | null
}) =>
  auditTrailLog.type === VoEAuditTrailType.CALL &&
  (auditTrailLog.title === VOE_AGENT_AUDIT_TRAIL_TITLE ||
    Boolean(
      auditTrailLog.message?.match(
        /^(VOE Agent call|Voice agent verification call|Voice agent call)\b/
      )
    ))

const INFO_COLLECTED_LABELS: Record<string, string> = {
  EMAIL_CAPTURED: 'Email captured',
  FAX_CAPTURED: 'Fax captured',
  PORTAL_CAPTURED: 'Portal identified',
  PHONE_CAPTURED: 'Phone captured',
  WRONG_TARGET: 'Wrong target',
  NEVER_WORKED_HERE: 'Never worked here',
}

/**
 * Badge text for one finding. `PORTAL_CAPTURED` names the platform when the
 * API could normalise it ("Portal identified: The Work Number"); an unknown
 * vendor keeps the bare badge, and the raw phrase the agent heard is in the
 * row text as "Portal: …".
 */
export const findingLabel = (
  finding: string,
  auditTrailLog: { portalProvider?: string | null }
): string => {
  const base = INFO_COLLECTED_LABELS[finding] ?? formatSnakeValue(finding)
  if (finding !== 'PORTAL_CAPTURED') return base
  const provider = knownPlatformLabel(auditTrailLog.portalProvider)
  return provider ? `${base}: ${provider}` : base
}

type UnappliedCapture = {
  field: 'email' | 'faxNumber'
  label: string
  value: string
}

/**
 * A referred number as it reads in the audit trail:
 * `+1 (555) 123-4567 ext 12 (Corporate HR), ask for Marcia`.
 */
export const formatReferredPhone = (auditTrailLog: {
  referredPhone?: string | null
  referredExtension?: string | null
  referredFor?: string | null
  referredContactName?: string | null
}): string | null => {
  const phone = cleanPhoneNumber(auditTrailLog.referredPhone)
  if (!phone) return null
  const parts = [formatInternationalPhoneNumber(phone) ?? phone]
  if (auditTrailLog.referredExtension?.trim()) {
    parts.push(`ext ${auditTrailLog.referredExtension.trim()}`)
  }
  if (auditTrailLog.referredFor?.trim()) {
    parts.push(`(${auditTrailLog.referredFor.trim()})`)
  }
  const label = parts.join(' ')
  return auditTrailLog.referredContactName?.trim()
    ? `${label}, ask for ${auditTrailLog.referredContactName.trim()}`
    : label
}

/**
 * Values the voice agent captured that disagree with what the VOE already
 * holds. We never overwrite the stored value automatically, so this is the
 * recruiter's one-click path to accept the agent's finding instead.
 *
 * Comparison uses the same normalizers the API promotes with, so the button is
 * only offered when the backend would also treat the values as different.
 *
 * The audit trail stores what the agent heard, unnormalized, so a finding can
 * carry transcription noise that is not an address or a number at all. Those
 * are dropped rather than offered: the update mutation runs the captured email
 * through `sanitizeEmail` and stores null when it fails, which would wipe the
 * address the VOE already had, and it stores a fax verbatim, which would leave
 * an unparseable string where a number belongs.
 */
export const getUnappliedCaptures = (
  auditTrailLog: {
    email?: string | null
    fax?: string | null
    infoCollected?: string[] | null
  },
  voe: { email?: string | null; faxNumber?: string | null }
): UnappliedCapture[] => {
  const findings: string[] = auditTrailLog.infoCollected || []
  const captures: UnappliedCapture[] = []

  // Offer the normalized value, so what a recruiter applies is the value the
  // comparison below was made against.
  const capturedEmail = sanitizeEmail(auditTrailLog.email)
  const capturedFax = cleanPhoneNumber(auditTrailLog.fax)

  if (
    findings.includes('EMAIL_CAPTURED') &&
    capturedEmail &&
    !emailMatches(capturedEmail, voe.email)
  ) {
    captures.push({
      field: 'email',
      label: 'Use this email',
      value: capturedEmail,
    })
  }

  // A call can capture both, so both are offered rather than only the first.
  if (
    findings.includes('FAX_CAPTURED') &&
    capturedFax &&
    !phoneMatches(capturedFax, voe.faxNumber)
  ) {
    captures.push({
      field: 'faxNumber',
      label: 'Use this fax',
      value: capturedFax,
    })
  }

  // A referred number is deliberately not offered here: it has its own row in
  // Company Information, where it can be called or edited without touching the
  // number the driver gave us (ENG-4702).
  return captures
}

/** Messages the post-call handler writes for a call that never reached a person. */
const MISSED_AGENT_CALL =
  /\|\s*(No answer|Voicemail left|Busy|Failed|Missed call)\b/

/**
 * The referred number from the latest voice-agent call that reached a person
 * on this VOE, if that call captured one. Shown as its own row in Company
 * Information so a recruiter can call it (or have the agent call it) without
 * first overwriting the number the driver gave us.
 *
 * Only the latest connected call counts: a newer conversation that captured
 * nothing means the earlier referral has been acted on or superseded, and a
 * deleted referral does not let an older one resurface. Voicemails and
 * no-answers are skipped so a failed attempt does not hide a live referral.
 * Older referrals stay in the audit trail.
 */
export const getLatestReferral = (
  auditTrailLogs: Array<{
    auditTrailId?: string | null
    type?: string | null
    title?: string | null
    message?: string | null
    contactDate?: string | null
    createdAt?: string | null
    referredPhone?: string | null
    referredExtension?: string | null
    referredFor?: string | null
    referredContactName?: string | null
    infoCollected?: string[] | null
  }>,
  voe: { phoneNumber?: string | null; contactName?: string | null }
): VoEReferral | null => {
  const latestConnected = [...auditTrailLogs]
    .filter(
      (log) =>
        isVoEAgentAuditLog(log) &&
        log.message !== VOE_AGENT_CALL_PENDING_MESSAGE &&
        !MISSED_AGENT_CALL.test(log.message ?? '')
    )
    .sort(
      (a, b) =>
        new Date(b.contactDate ?? b.createdAt ?? 0).getTime() -
        new Date(a.contactDate ?? a.createdAt ?? 0).getTime()
    )[0]
  if (!latestConnected) return null

  const phoneNumber = cleanPhoneNumber(latestConnected.referredPhone)
  if (
    !phoneNumber ||
    !(latestConnected.infoCollected || []).includes('PHONE_CAPTURED')
  ) {
    return null
  }

  return {
    auditTrailId: latestConnected.auditTrailId ?? null,
    phoneNumber,
    matchesPhone: phoneMatches(phoneNumber, voe.phoneNumber),
    extension: latestConnected.referredExtension ?? null,
    referredFor: latestConnected.referredFor ?? null,
    referredContactName: latestConnected.referredContactName ?? null,
    givenBy: voe.contactName ?? null,
    contactDate:
      latestConnected.contactDate ?? latestConnected.createdAt ?? null,
  }
}

/**
 * What `client.refetchQueries({ include })` takes for the drawer's `onRender`
 * list, which mixes operation names and `{ query, variables }` entries.
 */

export const AUDIT_TRAIL_PDF_MIN_ATTEMPTS = 3

/**
 * Rows the audit-trail PDF lists as attempts to reach the employer. Unlike the
 * header's `contactAttempts`, this includes ATS submissions, which count
 * toward the three-attempt flow and appear in the PDF.
 */
export const AUDIT_TRAIL_PDF_ATTEMPT_TYPES = ['CALL', 'EMAIL', 'FAX', 'ATS']

export const countAuditTrailPdfAttempts = (
  auditTrailLogs: { type?: string | null }[]
): number =>
  auditTrailLogs.filter((item) =>
    AUDIT_TRAIL_PDF_ATTEMPT_TYPES.includes(item.type)
  ).length

/**
 * The PDF is filed under the applicant's documents, so a VoE with no linked
 * applicant (possible on the company-wide drawer) cannot generate one.
 */
export const canGenerateAuditTrailPdf = ({
  contactAttempts,
  respondedAt,
  applicantId,
}: {
  contactAttempts: number
  respondedAt?: string | null
  applicantId?: string | null
}): boolean =>
  Boolean(applicantId) &&
  contactAttempts >= AUDIT_TRAIL_PDF_MIN_ATTEMPTS &&
  !respondedAt

export const refetchIncludeFor = (onRender?: any[]) =>
  (onRender ?? []).map((q) => (typeof q === 'string' ? q : q.query))

/**
 * The employee behind an audit row: who placed a call (dialed it, or pressed
 * "Call with agent"), or who logged a note, an email, or the completion.
 */
export const InitiatedByAvatar = ({
  employee,
  verb = 'Triggered by',
}: {
  employee: { firstName?: string | null; lastName?: string | null }
  verb?: 'Triggered by' | 'Logged by'
}) => {
  const name = buildFullName(employee)
  if (!name) return null
  const label = `${verb} ${name}`
  return (
    <Tooltip label={label} withArrow className="text-xs">
      <Avatar
        size={20}
        radius="sm"
        classNames={{
          placeholder:
            'bg-doubleNickel-brand-25 text-[10px] text-doubleNickel-brand-500',
        }}
        aria-label={label}
      >
        {getInitials(name)}
      </Avatar>
    </Tooltip>
  )
}

// ── Audit Trail Section (collapsible with count) ───────────────────────

const ActivityLogSection = ({
  auditTrailLogs,
  onContact,
  companyName,
  contactName,
  onRender,
  contactAttempts,
  voe,
  onApplyCapture,
  isApplyingCapture,
}: {
  auditTrailLogs: any[]
  onContact: () => void
  companyName?: string
  contactName?: string
  onRender?: any[]
  contactAttempts: number
  voe: {
    voeId: string
    status?: VoEStatus | string | null
    email?: string | null
    phoneNumber?: string | null
    faxNumber?: string | null
    respondedAt?: string | null
    applicantId?: string | null
  }
  onApplyCapture: (field: UnappliedCapture['field'], value: string) => void
  isApplyingCapture: boolean
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const showAuditTrailPdf = canGenerateAuditTrailPdf({
    contactAttempts: countAuditTrailPdfAttempts(auditTrailLogs),
    respondedAt: voe.respondedAt,
    applicantId: voe.applicantId,
  })
  const { handleOpen: openCallDrawer } = useCallDrawerContext()
  const client = useApolloClient()
  const outreach = useOutreachActions({
    voeId: voe.voeId,
    voeStatus: voe.status,
  })
  const upcomingContact = outreach.upcomingAction
    ? contactForAction(outreach.upcomingAction.type, voe)
    : null
  const hasRows = auditTrailLogs.length > 0 || !!outreach.upcomingAction

  return (
    <>
      <div className="flex items-center justify-between">
        <button
          className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
          onClick={() => setIsExpanded((v) => !v)}
        >
          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Text size="xs" fw={400} className="text-inherit">
            AUDIT TRAIL
            <span className="ml-1 text-doubleNickel-gray-400">
              ({contactAttempts})
            </span>
          </Text>
        </button>
        <div className="flex items-center gap-1">
          {/* The same refetch the section's own actions run, plus the
              automated actions, so an email the chain sent while the drawer
              was open shows up here and in the Send confirm. */}
          <AuditTrailRefreshButton
            onRefresh={() =>
              client.refetchQueries({
                include: [
                  ...refetchIncludeFor(onRender),
                  GET_VOE_ACTIONS,
                  GET_VOE_CONTACT_ATTEMPT_COUNT,
                ],
              })
            }
          />
          {showAuditTrailPdf && (
            <GenerateAuditTrailPdfButton
              voeId={voe.voeId}
              refetchQueries={Array.isArray(onRender) ? onRender : []}
            />
          )}
          <Menu position="bottom-end" shadow="md" width={190}>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Add to audit trail"
                className="h-6 w-6 hover:bg-doubleNickel-gray-100"
              >
                <Plus size={14} className="stroke-doubleNickel-gray-600" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item className="text-xs" onClick={onContact}>
                Log contact attempt
              </Menu.Item>
              <Menu.Item className="text-xs" onClick={outreach.openAdd}>
                Schedule outreach
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>

      <Collapse in={isExpanded}>
        {outreach.banner}
        {hasRows ? (
          <div className="relative pb-4 pl-4">
            {/* Vertical line */}
            <div className="absolute bottom-4 left-[11px] top-0 w-px bg-doubleNickel-gray-200" />

            <div className="flex flex-col gap-3">
              {outreach.upcomingAction && (
                <UpcomingOutreachRow
                  action={outreach.upcomingAction}
                  contact={upcomingContact}
                  onReschedule={outreach.openReschedule}
                  onCancel={outreach.cancel}
                  cancelling={
                    outreach.cancellingActionId ===
                    outreach.upcomingAction.voeActionId
                  }
                />
              )}
              {auditTrailLogs.map((auditTrailLog, index) => {
                const config =
                  auditTrailTypeConfig[auditTrailLog.type] ??
                  auditTrailTypeConfig.NOTE
                const isVoEAgentCall = isVoEAgentAuditLog(auditTrailLog)
                const contactInfo = isVoEAgentCall
                  ? [
                      'Contacted',
                      contactName && `(${contactName})`,
                      auditTrailLog.phone &&
                        formatInternationalPhoneNumber(auditTrailLog.phone),
                    ]
                      .filter(Boolean)
                      .join(' ')
                  : getInfoBasedOnType(auditTrailLog, {
                      companyName,
                      contactName,
                    })
                const isPendingAgentCall =
                  isVoEAgentCall &&
                  auditTrailLog.message === VOE_AGENT_CALL_PENDING_MESSAGE
                const capturedEmail = auditTrailLog.email?.trim()
                const referredPhone = formatReferredPhone(auditTrailLog)
                const isSystemCall =
                  auditTrailLog.type === VoEAuditTrailType.CALL &&
                  !!auditTrailLog.activityLogId
                const isAutomatedEmail =
                  auditTrailLog.type === VoEAuditTrailType.EMAIL &&
                  auditTrailLog.title?.startsWith('Automated')
                // An automated send carries nothing but its address; the
                // captured email repeats the contact line for EMAIL rows.
                const contentParts = isAutomatedEmail
                  ? [
                      capturedEmail
                        ? `Sent to ${capturedEmail}`
                        : auditTrailLog.message,
                    ]
                  : [
                      isPendingAgentCall ? null : auditTrailLog.message,
                      contactInfo &&
                      !auditTrailLog.message?.includes(contactInfo)
                        ? contactInfo
                        : null,
                      capturedEmail &&
                      !auditTrailLog.message?.includes(capturedEmail) &&
                      !emailMatches(capturedEmail, contactInfo)
                        ? `Email: ${capturedEmail}`
                        : null,
                      referredPhone &&
                      !auditTrailLog.message?.includes('Referred to:')
                        ? `Referred to: ${referredPhone}`
                        : null,
                    ]
                const content = contentParts.filter(Boolean).join(' ')

                const findings: string[] = auditTrailLog.infoCollected || []
                const unappliedCaptures = getUnappliedCaptures(
                  auditTrailLog,
                  voe
                )

                const handleOpenCall = isSystemCall
                  ? () =>
                      openCallDrawer(auditTrailLog.activityLogId, () => {
                        if (onRender) {
                          client.refetchQueries({
                            include: refetchIncludeFor(onRender),
                          })
                        }
                      })
                  : undefined

                return (
                  <div
                    key={`audit-${index}`}
                    className={`relative flex gap-2.5 ${
                      isSystemCall
                        ? '-mx-1 cursor-pointer rounded px-1 hover:bg-doubleNickel-gray-50'
                        : ''
                    }`}
                    {...(isSystemCall && {
                      role: 'button',
                      tabIndex: 0,
                      onClick: handleOpenCall,
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleOpenCall()
                        }
                      },
                    })}
                  >
                    {/* Icon bullet */}
                    <div
                      className={`relative z-10 -ml-4 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full ${config.bgClass}`}
                    >
                      <span className={config.iconClass}>{config.icon}</span>
                    </div>

                    {/* Content */}
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex h-[22px] items-center justify-between gap-2">
                        <Text
                          size="xs"
                          fw={500}
                          className="truncate text-doubleNickel-gray-900"
                        >
                          {isPendingAgentCall
                            ? VOE_AGENT_CALL_PENDING_MESSAGE
                            : config.label}
                        </Text>
                        <div className="flex shrink-0 items-center gap-1">
                          {isVoEAgentCall && (
                            <ActorBadge
                              variant="ai"
                              label="Placed by the VoE voice agent"
                              className={ACTOR_MARKER_CLASS}
                            />
                          )}
                          {isAutomatedEmail && (
                            <ActorBadge
                              variant="auto"
                              label={AUTOMATED_OUTREACH_LABEL}
                              className={ACTOR_MARKER_CLASS}
                            />
                          )}
                          {auditTrailLog.initiatedBy && !isAutomatedEmail && (
                            <InitiatedByAvatar
                              employee={auditTrailLog.initiatedBy}
                              verb={
                                isSystemCall || isVoEAgentCall
                                  ? 'Triggered by'
                                  : 'Logged by'
                              }
                            />
                          )}
                          {isSystemCall &&
                            !isVoEAgentCall &&
                            !auditTrailLog.initiatedBy && (
                              <span className="inline-flex h-[18px] items-center rounded-full bg-doubleNickel-brand-100 px-1.5 text-[10px] font-medium leading-none text-doubleNickel-brand-700">
                                System
                              </span>
                            )}
                          {auditTrailLog.createdAt && (
                            <Text
                              size="xs"
                              className={AUDIT_TRAIL_TIMESTAMP_CLASS}
                            >
                              {formatDateCustom(
                                auditTrailLog.createdAt,
                                OUTREACH_DATE_TIME_FORMAT
                              )}
                            </Text>
                          )}
                        </div>
                      </div>
                      {content && (
                        <Text size="xs" className="text-doubleNickel-gray-600">
                          {content}
                        </Text>
                      )}
                      {findings.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1">
                          {findings.map((finding) => (
                            <span
                              key={finding}
                              className="rounded bg-doubleNickel-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-doubleNickel-gray-600"
                            >
                              {findingLabel(finding, auditTrailLog)}
                            </span>
                          ))}
                        </div>
                      )}
                      {unappliedCaptures.map((capture) => (
                        <div
                          key={capture.field}
                          className="flex flex-wrap items-center gap-1.5 pt-0.5"
                        >
                          <Text
                            size="xs"
                            className="text-doubleNickel-gray-500"
                          >
                            {capture.field === 'faxNumber'
                              ? formatInternationalPhoneNumber(capture.value)
                              : capture.value}
                          </Text>
                          <button
                            type="button"
                            disabled={isApplyingCapture}
                            className="rounded px-1.5 py-0.5 text-[10px] font-medium text-doubleNickel-brand-600 hover:bg-doubleNickel-brand-25 disabled:opacity-50"
                            onClick={(event) => {
                              event.stopPropagation()
                              onApplyCapture(capture.field, capture.value)
                            }}
                          >
                            {capture.label}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <Text
            size="xs"
            className="py-1 text-center text-doubleNickel-gray-400"
          >
            No contact attempts yet
          </Text>
        )}
        <ScheduledActionsTable
          actions={outreach.actions}
          onReschedule={outreach.openReschedule}
          onCancel={outreach.cancel}
          cancellingActionId={outreach.cancellingActionId}
        />
      </Collapse>
      {outreach.modals}
    </>
  )
}

// ── Overview Section (collapsible) ──────────────────────────────────────
const OverviewSection = ({
  voe,
  isUpdatingStatus,
  handleStatusUpdate,
  verificationPeriodMonths,
  contactAttempts,
}: {
  voe: VerificationOfEmployment
  isUpdatingStatus: boolean
  handleStatusUpdate: (status: VoEStatus) => void
  verificationPeriodMonths: number
  contactAttempts: number
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  return (
    <>
      <button
        className="flex items-center gap-1 text-doubleNickel-gray-500 hover:text-doubleNickel-gray-700"
        onClick={() => setIsExpanded((v) => !v)}
      >
        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Text size="xs" fw={400} className="text-inherit">
          OVERVIEW
        </Text>
      </button>

      <Collapse in={isExpanded}>
        <div className="flex flex-col space-y-4">
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Status
            </Text>
            <VoEStatusMenu
              status={voe.status}
              disabled={isUpdatingStatus}
              onSelect={handleStatusUpdate}
            />
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Start Date
            </Text>
            <Text size="xs" fw={300}>
              {voe.requestContent?.startDate
                ? dayjs(voe.requestContent.startDate).utc().format('MM/YYYY')
                : '-'}
            </Text>
          </div>
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Text size="xs" fw={300}>
                End Date
              </Text>
              {isExpiredEmployment(
                voe.requestContent?.endDate,
                verificationPeriodMonths
              ) && (
                <Tooltip
                  label={`This employment ended more than ${formatVerificationPeriod(
                    verificationPeriodMonths
                  )} ago`}
                  position="top"
                >
                  <CircleAlert
                    size={14}
                    className="stroke-doubleNickel-warning-500"
                  />
                </Tooltip>
              )}
            </div>
            <Text size="xs" fw={300}>
              {voe.requestContent?.endDate
                ? dayjs(voe.requestContent.endDate).utc().format('MM/YYYY')
                : 'Present'}
            </Text>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Job Duration
            </Text>
            <Text size="xs" fw={300}>
              {voe.requestContent
                ? calculateAverageJobDuration([voe.requestContent])
                : '-'}
            </Text>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Contact Employer
            </Text>
            <Text size="xs" fw={300}>
              {voe.shouldContactEmployer == undefined ||
              voe.shouldContactEmployer == null
                ? '-'
                : voe.shouldContactEmployer
                ? 'Yes'
                : 'No'}
            </Text>
          </div>
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300}>
              Contact Attempts
            </Text>
            <Text size="xs" fw={300}>
              {contactAttempts}
            </Text>
          </div>
        </div>
      </Collapse>
    </>
  )
}

interface VoEDetailsProps {
  voe?: VerificationOfEmployment
  opened: boolean
  onClose: () => void
  onContact: (voe) => void
  onViewDetail: (voe) => void
  onRender?: any[]
  applicantId: string
  onMarkComplete?: (voe: VerificationOfEmployment) => void
  verificationPeriodMonths?: number
  companyPhoneNumber?: string
}

const VoEDetails = ({
  voe,
  opened,
  onClose,
  onContact,
  onViewDetail,
  onRender,
  applicantId,
  onMarkComplete,
  verificationPeriodMonths = DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  companyPhoneNumber,
}: VoEDetailsProps) => {
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [completionModalOpened, completionModal] = useDisclosure(false)
  const [emailModalOpened, emailModal] = useDisclosure(false)
  const { guardContact, noContactPrompt } = useNoContactGuard()
  const { guardScheduledOutreach, scheduledOutreachPrompt } =
    useScheduledOutreachGuard()

  // Phone device for calling directly from the drawer
  const phoneDeviceContext = usePhoneDevice()
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const client = useApolloClient()
  const { opened: callDrawerOpened } = useCallDrawerContext() ?? {}

  // Refetch VoE data when a call disconnects (mirrors OutboundVoETable pattern)
  const prevCallRef = useRef(phoneDeviceContext?.call)
  useEffect(() => {
    const wasOnCall = prevCallRef.current != null
    const callEnded = phoneDeviceContext?.call == null
    const isVoECall = phoneDeviceContext?.voeId != null
    if (wasOnCall && callEnded && isVoECall && onRender) {
      setTimeout(() => {
        client.refetchQueries({ include: refetchIncludeFor(onRender) })
      }, 2000)
    }
    prevCallRef.current = phoneDeviceContext?.call ?? null
  }, [phoneDeviceContext?.call])

  // Email sending
  const { currentUser } = useAuth()
  const [sendInstructionsEmail, { loading: loadingSendEmail }] = useMutation(
    SEND_INSTRUCTIONS_EMAIL_FOR_OUTGOING_VOE,
    {
      refetchQueries: onRender,
      onCompleted: () => {
        toast('Email sent', 'success')
        emailModal.close()
      },
      onError: () => {
        toast('Unable to send email, please try again.', 'error')
      },
    }
  )

  // Fetch address lazily only when drawer is open
  const { data: voeAddressData } = useQuery(GET_VOE_ADDRESS, {
    variables: { id: voe?.voeId },
    skip: !opened || !voe?.voeId,
  })
  const companyAddress =
    voeAddressData?.verificationOfEmployment?.employmentExperience?.address ||
    voeAddressData?.verificationOfEmployment?.companyAddress

  const [updateVoeStatus] = useMutation(UPDATE_VOE, {
    refetchQueries: onRender,
    onCompleted: () => {
      toast('VoE status updated successfully', 'success')
      setIsUpdatingStatus(false)
    },
    onError: (error) => {
      toast(error.message || 'Error updating VoE status', 'error')
      setIsUpdatingStatus(false)
    },
  })

  const [applyCapturedContactInfo, { loading: loadingApplyCapture }] =
    useMutation(UPDATE_VOE, {
      refetchQueries: onRender,
      onCompleted: () => {
        toast('Contact info updated', 'success')
      },
      onError: (error) => {
        toast(error.message || 'Error updating contact info', 'error')
      },
    })

  const [markAsCompleted, { loading: loadingMarkComplete }] = useMutation(
    MARK_VOE_AS_COMPLETED,
    {
      refetchQueries: onRender,
      onCompleted: () => {
        toast('VoE marked as completed successfully', 'success')
        completionModal.close()
      },
      onError: (error) => {
        toast(error.message || 'Error marking VoE as completed', 'error')
      },
    }
  )

  // A manual Send asks first when automation has already emailed this VoE
  // (or has one queued): a manual touch makes the employer's answer ASSISTED.
  const { config: outreachConfig } = useCompanyOutreachConfig()
  const automationOn = Boolean(outreachConfig?.enabled)
  const automatedEmails = useAutomatedEmailActions(voe?.voeId, {
    skip: !opened || !automationOn,
  })
  const fetchLatestAutomatedEmails = useFetchLatestAutomatedEmails(voe?.voeId)
  const manualSendConfirm = useManualSendConfirm({
    voeId: voe?.voeId,
    email: voe?.email,
    automationOn,
    summary: automatedEmails,
    fetchLatestSummary: fetchLatestAutomatedEmails,
  })

  // The address typed in the outreach panel, carried into the composer.
  const [composerEmail, setComposerEmail] = useState<string | null>(null)
  // Automation schedules its steps in a background run a moment after the
  // start, so the next-action line is read again once that has landed.
  const outreachEmail = useAutomatedOutreachEmail({
    component: 'VoEDetails',
    onStarted: () => {
      const refetchAll = () =>
        client.refetchQueries({
          include: [...refetchIncludeFor(onRender), GET_VOE_ACTIONS],
        })
      refetchAll()
      setTimeout(refetchAll, 2500)
    },
  })

  if (!voe) return null

  const auditTrailLogs = voe.auditTrailLogs || []
  const neverWorkedHere = hasNeverWorkedHereFinding(auditTrailLogs)
  const contactAttempts = auditTrailLogs.filter(
    (item) =>
      item.type === 'CALL' || item.type === 'EMAIL' || item.type === 'FAX'
  ).length

  const handleNoContactGuard = (action: () => void) => guardContact(voe, action)

  const handleCall = (phoneNumber?: string) => {
    handleNoContactGuard(() => {
      if (phoneDeviceContext?.phoneDevice?.isBusy) return
      dispatchPhoneAction({
        type: PhoneDeviceActionType.CALL,
        payload: {
          phone: unformatPhoneNumber(phoneNumber ?? voe.phoneNumber),
          ...(applicantId && { applicantId }),
          applicantFullName: (voe as any).applicant
            ? buildFullName((voe as any).applicant)
            : (voe as any).driverName || voe.companyName,
          voeId: voe.voeId,
          companyName: voe.companyName,
          voeEmail: voe.email,
        },
      })
    })
  }

  // The No Contact guard first, as for every contact action; then the outreach
  // panel when the company has outreach set up, or the automation confirm and
  // the modal when it does not. "Send manually" from the panel skips the
  // confirm, since the panel already showed what automation is doing.
  const handleEmail = () =>
    handleNoContactGuard(() => {
      const openComposer = (email: string | null) => {
        setComposerEmail(email)
        emailModal.open()
      }
      outreachEmail.requestEmail(
        {
          voeId: voe.voeId,
          email: voe.email,
          companyName: voe.companyName,
          applicantName,
        },
        {
          sendManually: ({ email }) => openComposer(email ?? null),
          notAutomated: () =>
            manualSendConfirm.requestSend(() => openComposer(null)),
        }
      )
    })

  const companyName = currentUser?.companyName
  const applicantName = (voe as any).applicant
    ? buildFullName((voe as any).applicant)
    : (voe as any).driverName || ''
  const emailBody = buildVoEInstructionsEmailBody({
    companyName,
    applicantName,
    pastEmployerName: voe.companyName ?? '',
    recruiterEmail: currentUser?.email,
    companyPhone: companyPhoneNumber ?? '',
  })

  const handleStatusUpdate = (newStatus: VoEStatus) => {
    if (newStatus === VoEStatus.COMPLETED) {
      if (voe.responseContent?.voeResponseContentId) {
        // Already completed via Double Nickel form — skip the modal
        markAsCompleted({
          variables: {
            id: voe.voeId,
            input: { completionMethod: 'DOUBLE_NICKEL_FORM' },
          },
        })
      } else if (onMarkComplete) {
        onMarkComplete(voe)
      } else {
        completionModal.open()
      }
      return
    }
    setIsUpdatingStatus(true)
    updateVoeStatus({
      variables: {
        id: voe.voeId,
        input: {
          status: newStatus,
        },
      },
    })
  }

  return (
    <>
      <DnDrawer
        opened={opened}
        onClose={onClose}
        stackId="voe-details-drawer"
        lockScroll={!callDrawerOpened}
        classNames={{
          content: 'flex flex-col overflow-hidden',
          body: 'flex min-h-0 flex-1 flex-col',
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col space-y-4">
          {/* HEADER */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center gap-2">
              <AdminContainer>
                <Tooltip label="Copy VoE ID">
                  <Copy
                    size={12}
                    strokeWidth={1}
                    className="cursor-pointer hover:text-doubleNickel-gray-500"
                    onClick={() => {
                      navigator.clipboard.writeText(voe.voeId)
                      toast('Copied VoE ID to clipboard', 'success')
                    }}
                  />
                </Tooltip>
              </AdminContainer>
              <Text size="lg" fw={500}>
                {voe.companyName || 'Verification of Employment'}
              </Text>
            </div>

            <VoeActions voe={voe} onViewDetail={onViewDetail} />
          </div>
          <NextActionLine voe={voe} />
          <Divider variant="dashed" />

          {/* BODY */}
          <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-y-auto overflow-x-hidden pb-4">
            {/* Overview Section */}
            <OverviewSection
              voe={voe}
              isUpdatingStatus={isUpdatingStatus}
              handleStatusUpdate={handleStatusUpdate}
              verificationPeriodMonths={verificationPeriodMonths}
              contactAttempts={contactAttempts}
            />

            {neverWorkedHere && (
              <div className="flex flex-row items-start gap-3 rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-25 px-4 py-3">
                <CircleAlert
                  size={16}
                  className="mt-0.5 shrink-0 stroke-doubleNickel-warning-600"
                />
                <div className="flex flex-col gap-0.5">
                  <Text
                    size="sm"
                    className="font-semibold text-doubleNickel-gray-900"
                  >
                    Employer says the driver never worked there
                  </Text>
                  <Text size="xs" className="text-doubleNickel-gray-600">
                    No employment details were provided. Review before
                    completing or declining this request.
                  </Text>
                </div>
              </div>
            )}

            <Divider variant="dashed" />

            {/* Applicant Info Section */}
            <ApplicantInfoSection applicantId={applicantId} opened={opened} />

            {/* Verification Details Section */}
            <VerificationDetailsSection
              voe={voe}
              referral={getLatestReferral(auditTrailLogs, voe)}
              onRender={onRender}
              onCall={handleCall}
              onEmail={handleEmail}
              guardContact={handleNoContactGuard}
              companyAddress={companyAddress}
            />

            <Divider variant="dashed" />

            {/* Documents Section */}
            <VoEDocumentsSection
              voe={voe}
              applicantId={applicantId}
              onRender={onRender}
            />

            {/* Activity Log Section */}
            <ActivityLogSection
              auditTrailLogs={auditTrailLogs}
              onContact={() => onContact(voe)}
              companyName={voe.companyName}
              contactName={voe.contactName}
              onRender={onRender}
              contactAttempts={contactAttempts}
              voe={{
                voeId: voe.voeId,
                status: voe.status,
                email: voe.email,
                phoneNumber: voe.phoneNumber,
                faxNumber: voe.faxNumber,
                respondedAt: voe.respondedAt,
                applicantId: voe.applicant?.applicantId || applicantId,
              }}
              isApplyingCapture={loadingApplyCapture}
              onApplyCapture={(field, value) =>
                applyCapturedContactInfo({
                  variables: { id: voe.voeId, input: { [field]: value } },
                })
              }
            />
          </div>
        </div>
      </DnDrawer>
      {!onMarkComplete && (
        <CompletionMethodModal
          opened={completionModalOpened}
          onClose={completionModal.close}
          onSubmit={(values) => {
            markAsCompleted({
              variables: {
                id: voe.voeId,
                input: toMarkVoEAsCompletedInput(values),
              },
            })
          }}
          loading={loadingMarkComplete}
          {...voeCompletionContext(voe)}
        />
      )}
      {emailModalOpened && (
        <EmailSenderModal
          from={currentUser?.email}
          to={composerEmail || voe.email || ''}
          title="Send instructions via Email"
          subject={`Employment verification request from ${companyName}`}
          body={emailBody}
          isOpen={emailModalOpened}
          onClose={emailModal.close}
          onSend={({ email }) =>
            guardScheduledOutreach(
              {
                voeId: voe.voeId,
                kind: 'EMAIL',
                savedEmail: voe.email,
                recipient: email,
              },
              {
                manual: () =>
                  sendInstructionsEmail({
                    variables: { emailTo: email, voeId: voe.voeId },
                  }),
                onSentNow: emailModal.close,
              }
            )
          }
          loading={loadingSendEmail}
        />
      )}
      {scheduledOutreachPrompt}
      {outreachEmail.panel}
      <DnPrompt {...manualSendConfirm.promptProps} />
      {noContactPrompt}
    </>
  )
}

export default VoEDetails
